import React, { useRef, useImperativeHandle, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Link,
  Tooltip,
  useTheme
} from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import TimelineBar from './TimelineBar'
import StatusColumn from './StatusColumn'
import { ProcessedInitiative } from '../utils/dataProcessing'
import { calculateTodayPosition, getTodayMarkerStyles, calculateOptimalScrollPosition } from '../utils/todayMarkerUtils'
import { parseISO, isValid, format, differenceInMonths, differenceInDays, addMonths, startOfMonth } from 'date-fns'


interface TimelineTableProps {
  data: ProcessedInitiative[]
  isLoading?: boolean
}

export interface TimelineTableRef {
  scrollToToday: () => void
}

const TimelineTable = forwardRef<TimelineTableRef, TimelineTableProps>(({ data, isLoading = false }, ref) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  // Ref for the TableContainer to enable programmatic scrolling
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Ref to store the scroll function to avoid race conditions
  const scrollToTodayRef = useRef<(() => void) | null>(null)

  // Expose the scrollToToday function to parent components
  // This MUST be called before any early returns to comply with Rules of Hooks
  useImperativeHandle(ref, () => ({
    scrollToToday: () => {
      // No-op if data isn't ready yet or ref isn't available
      if (!tableContainerRef.current || !data || data.length === 0) {
        return
      }
      // Call the latest scroll function from the ref
      if (scrollToTodayRef.current) {
        scrollToTodayRef.current()
      }
    }
  }), [data])

  // Helper function to format status display
  const formatStatus = (status: string | { name: string }) => {
    return typeof status === 'string' ? status : status?.name || 'Unknown'
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              border: '3px solid',
              borderColor: 'primary.light',
              borderTopColor: 'primary.main',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography variant="h6" color="text.secondary">
            Loading Timeline View
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Fetching timeline data and preparing visualization...
        </Typography>
      </Box>
    )
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No initiatives to display in timeline view
        </Typography>
      </Box>
    )
  }

  // Calculate ultra-tight date range for maximum space utilization
  const calculateDateRange = () => {
    const timelineBars: { start: Date, end: Date }[] = []

    // Collect ONLY the actual timeline bars that will be displayed
    data.forEach(initiative => {
      const hasTimeline = initiative.timeline?.hasCompleteTimeline ||
                         (initiative.startDate && initiative.endDate)

      if (hasTimeline) {
        const startDate = initiative.timeline?.startDate || initiative.startDate
        const endDate = initiative.timeline?.endDate || initiative.endDate

        if (startDate && endDate) {
          const parsedStart = parseISO(startDate)
          const parsedEnd = parseISO(endDate)

          if (isValid(parsedStart) && isValid(parsedEnd)) {
            timelineBars.push({ start: parsedStart, end: parsedEnd })
          }
        }
      }
    })

    if (timelineBars.length === 0) {
      return { minDate: new Date(), maxDate: new Date() }
    }

    // Get the absolute earliest start and latest end from actual timeline bars
    const earliestStart = new Date(Math.min(...timelineBars.map(bar => bar.start.getTime())))
    const latestEnd = new Date(Math.max(...timelineBars.map(bar => bar.end.getTime())))

    // Include today's date in the timeline range to ensure Today marker is visible
    const today = new Date()
    const adjustedMinDate = today < earliestStart ? today : earliestStart
    const adjustedMaxDate = today > latestEnd ? today : latestEnd

    return {
      minDate: adjustedMinDate,
      maxDate: adjustedMaxDate
    }
  }

  const { minDate, maxDate } = calculateDateRange()

  // Generate optimized timeline scale markers with maximum container width
  const generateTimelineScale = () => {
    if (!minDate || !maxDate) {
      return { markers: [], containerWidth: 600 }
    }

    const markers = []
    const totalMonths = differenceInMonths(maxDate, minDate)

    // Further optimized spacing for more compact timeline
    // Provides adequate granularity while minimizing scrolling even more
    const monthSpacing = 50 // More compact spacing: still accurate but more user-friendly
    const minContainerWidth = 500 // Reduced minimum width for compactness

    // Calculate container width with optimized spacing
    const calculatedWidth = totalMonths * monthSpacing
    const containerWidth = Math.max(minContainerWidth, calculatedWidth)

    if (totalMonths <= 6) {
      // Show monthly markers for short to medium timelines (≤6 months)
      let currentDate = startOfMonth(minDate)
      while (currentDate <= maxDate) {
        const position = ((currentDate.getTime() - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * containerWidth
        markers.push({
          position,
          label: format(currentDate, 'MMM yyyy'),
          date: currentDate
        })
        currentDate = addMonths(currentDate, 1)
      }
    } else if (totalMonths <= 18) {
      // Show bi-monthly markers for medium timelines (6-18 months)
      let currentDate = startOfMonth(minDate)
      while (currentDate <= maxDate) {
        const position = ((currentDate.getTime() - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * containerWidth
        markers.push({
          position,
          label: format(currentDate, 'MMM yyyy'),
          date: currentDate
        })
        currentDate = addMonths(currentDate, 2)
      }
    } else {
      // Show quarterly markers for longer timelines (>18 months)
      // But with much better spacing for accurate positioning
      let currentDate = startOfMonth(minDate)
      while (currentDate <= maxDate) {
        const position = ((currentDate.getTime() - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * containerWidth
        markers.push({
          position,
          label: format(currentDate, 'MMM yyyy'),
          date: currentDate
        })
        currentDate = addMonths(currentDate, 3)
      }
    }

    return { markers, containerWidth }
  }

  const { markers: timelineMarkers, containerWidth } = generateTimelineScale()



  // Function to scroll the timeline to center the Today marker
  const scrollToToday = () => {
    if (!tableContainerRef.current || !minDate || !maxDate) {
      return
    }

    const container = tableContainerRef.current
    const viewportWidth = container.clientWidth
    const actualScrollWidth = container.scrollWidth

    // Calculate optimal scroll position (30% past context, 70% future context)
    // Use the actual scroll width instead of calculated container width
    const scrollPosition = calculateOptimalScrollPosition(
      minDate,
      maxDate,
      actualScrollWidth,
      viewportWidth,
      0.3 // Show 30% past timeline, 70% future timeline
    )

    if (scrollPosition !== null) {
      // Smooth scroll to the calculated position
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  // Store the scroll function in a ref to avoid race conditions
  // This ensures the latest version is always available
  React.useEffect(() => {
    scrollToTodayRef.current = scrollToToday
  }, [minDate, maxDate, containerWidth])

  return (
    <Box sx={{ position: 'relative' }}>
      <TableContainer
        ref={tableContainerRef}
        sx={{
          maxHeight: 800,
          overflowX: 'auto', // Enable horizontal scrolling for wide timelines
          overflowY: 'auto'
        }}>
        <Table stickyHeader sx={{
          '& .MuiTableCell-root': {
            py: 0.75, // Reduced from 1 to 0.75 (25% reduction)
            px: 1.5
          },
          minWidth: `${containerWidth + 350}px` // Ensure table is wide enough for the timeline
        }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: 70,
                minWidth: 70,
                fontWeight: 'bold',
                borderBottom: 2,
                borderColor: 'divider',
                verticalAlign: 'top',
                position: 'sticky',
                left: 0,
                zIndex: 3, // Higher than other sticky elements
                whiteSpace: 'nowrap',
                backgroundColor: 'background.paper', // Ensure solid background
                backgroundClip: 'padding-box' // Prevent background bleeding
              }}
            >
              Rank
            </TableCell>
            <TableCell
              sx={{
                width: 350,
                minWidth: 350,
                maxWidth: 350,
                fontWeight: 'bold',
                borderBottom: 2,
                borderColor: 'divider',
                verticalAlign: 'top',
                position: 'sticky',
                left: 70, // Position after Epic Rank column (updated to match new width)
                zIndex: 3, // Higher than other sticky elements
                borderRight: '1px solid',
                borderRightColor: 'divider',
                backgroundColor: 'background.paper', // Ensure solid background
                backgroundClip: 'padding-box' // Prevent background bleeding
              }}
            >
              Summary
            </TableCell>

            {/* Status Column Header */}
            <TableCell
              sx={{
                width: 110,
                minWidth: 110,
                maxWidth: 110,
                fontWeight: 'bold',
                borderBottom: 2,
                borderColor: 'divider',
                verticalAlign: 'top',
                textAlign: 'center',
                position: 'sticky',
                left: 420, // Position after Epic Rank (70px) + Summary (350px)
                zIndex: 3, // Same as other sticky headers
                backgroundColor: 'background.paper',
                backgroundClip: 'padding-box',
                borderRight: '1px solid',
                borderRightColor: 'divider'
              }}
            >
              Status
            </TableCell>

            <TableCell
              sx={{
                width: 'auto',
                minWidth: 'fit-content', // Fit content instead of fixed viewport width
                maxWidth: `${containerWidth + 150}px`, // Limit to calculated width plus margin
                fontWeight: 'bold',
                borderBottom: 2,
                borderColor: 'divider',
                verticalAlign: 'top',
                pl: 2 // Add left padding for proper spacing from Status column border
                // Removed overflow: 'auto' to disable horizontal scrolling
              }}
            >
              <Box sx={{ position: 'relative', height: 50 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Timeline ({format(minDate, 'MMM yyyy')} - {format(maxDate, 'MMM yyyy')})
                </Typography>

                {/* Timeline Scale - Theme Aware */}
                <Box sx={{
                  position: 'relative',
                  height: 25,
                  width: `${containerWidth}px`, // Fixed width to fit screen
                  maxWidth: `${containerWidth}px`, // Ensure it doesn't exceed calculated width
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  borderRadius: 1,
                  ml: 1, // Margin to match the timeline body spacing
                  pl: 1 // Add left padding to prevent text truncation
                  // Removed overflowX: 'auto' to disable horizontal scrolling
                }}>
                  {timelineMarkers.map((marker, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'absolute',
                        left: marker.position,
                        top: 0,
                        height: '100%',
                        borderLeft: '1px solid',
                        borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'divider',
                        pl: 0.5
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {marker.label}
                      </Typography>
                    </Box>
                  ))}

                  {/* Today Marker */}
                  {(() => {
                    const todayPosition = calculateTodayPosition(minDate, maxDate, containerWidth)

                    if (todayPosition === null) {
                      return null
                    }

                    const styles = getTodayMarkerStyles(isDarkMode)

                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: todayPosition,
                          top: -25, // Position at header level
                          height: '2000px', // Extended height to span through all rows
                          width: '1px',
                          backgroundColor: styles.header.backgroundColor,
                          zIndex: 10, // Above timeline markers but below content
                          pointerEvents: 'none' // Don't interfere with interactions
                        }}
                      >
                        {/* Today Label - positioned above the line */}
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            top: -3.6, // Position above the line (moved down 0.2 inch = 14.4px from -18)
                            left: '50%', // Center horizontally on the line
                            transform: 'translateX(-50%)', // Center the text on the line
                            fontSize: '0.65rem',
                            color: styles.header.labelColor,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            backgroundColor: styles.header.labelBackground,
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            border: `1px solid ${styles.header.labelBorder}`,
                            zIndex: 11 // Above the line
                          }}
                        >
                          Today
                        </Typography>
                      </Box>
                    )
                  })()}
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data && Array.isArray(data) ? data.map((initiative) => {
            if (!initiative || !initiative.id) {
              console.warn('Invalid initiative data:', initiative)
              return null
            }

            const statusDisplay = formatStatus(initiative.status || 'Unknown')
            const hasTimeline = initiative.timeline?.hasCompleteTimeline ||
                               (initiative.startDate && initiative.endDate)

            // Get timeline data from either timeline API or direct fields
            const timelineStartDate = initiative.timeline?.startDate || initiative.startDate
            const timelineEndDate = initiative.timeline?.endDate || initiative.endDate
            
            return (
              <TableRow
                key={initiative.id}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                  // Theme-aware row colors
                }}
              >
                {/* Epic Rank */}
                <TableCell sx={{
                  textAlign: 'center',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: 'background.paper', // Ensure solid background
                  backgroundClip: 'padding-box' // Prevent background bleeding
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: '400', fontSize: '0.875rem' }}>
                      {initiative.epicRank === '999' ? '-' : initiative.epicRank}
                    </span>
                  </Box>
                </TableCell>

                {/* Summary with Key */}
                <TableCell sx={{
                  width: 350,
                  minWidth: 350,
                  maxWidth: 350,
                  position: 'sticky',
                  left: 70, // Position after Epic Rank column (updated to match new width)
                  zIndex: 2,
                  borderRight: '1px solid',
                  borderRightColor: 'divider',
                  backgroundColor: 'background.paper', // Ensure solid background
                  backgroundClip: 'padding-box' // Prevent background bleeding
                }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/initiative/${initiative.key}`);
                        }}
                      >
                        {initiative.key}
                      </Typography>
                      <Tooltip title="Open in JIRA">
                        <Link
                          href={`https://redislabs.atlassian.net/browse/${initiative.key}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNew sx={{ fontSize: 14 }} />
                        </Link>
                      </Tooltip>
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {initiative.summary}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Status Column */}
                <TableCell sx={{
                  width: 110,
                  minWidth: 110,
                  maxWidth: 110,
                  textAlign: 'center',
                  position: 'sticky',
                  left: 420, // Position after Epic Rank (70px) + Summary (350px)
                  zIndex: 2, // Same as other sticky body cells
                  backgroundColor: 'background.paper',
                  backgroundClip: 'padding-box',
                  borderRight: '1px solid',
                  borderRightColor: 'divider'
                }}>
                  <StatusColumn
                    status={statusDisplay}
                  />
                </TableCell>

                {/* Timeline */}
                <TableCell sx={{
                  width: 'auto',
                  maxWidth: `${containerWidth + 150}px`, // Limit to calculated width plus margin
                  position: 'relative', // Enable absolute positioning for Today marker
                  zIndex: 1, // Lower than sticky columns to prevent overlap
                  pl: 2 // Add left padding for proper spacing from Status column border
                  // Removed overflow: 'auto' to disable horizontal scrolling
                }}>
                  <Box sx={{ position: 'relative', ml: 1, pl: 1 }}> {/* Match the scale padding for alignment */}
                    <TimelineBar
                      startDate={timelineStartDate}
                      endDate={timelineEndDate}
                      status={statusDisplay}
                      summary={initiative.summary}
                      epicRank={initiative.epicRank}
                      hasCompleteTimeline={hasTimeline}
                      minDate={minDate}
                      maxDate={maxDate}
                      containerWidth={containerWidth}
                      totalStoryPoints={initiative.totalStoryPoints}
                      completedStoryPoints={initiative.completedStoryPoints}
                    />



                    {/* Today Marker is now rendered as a single continuous line from the header */}
                  </Box>
                </TableCell>
              </TableRow>
            )
          }).filter(Boolean) : (
            <TableRow>
              <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  )
})

TimelineTable.displayName = 'TimelineTable'

export default TimelineTable
